<template>
  <div id="customer-overview-page" class="viewport viewport-with-bottom-nav viewport-compact">
    <pull-refresh :on-refresh="onRefresh">
      <div class="d-flex justify-content-between align-items-center">
        <div class="icon icon-lg">
          <logo-element></logo-element>
        </div>
        <div>
          <router-link :to="{ name: 'account' }" class="nav-top-link pl-4">
            <div class="small text-muted mr-2" id="customer-overview-page-name">
              <div>{{ state.currentUser.firstName }}</div>
            </div>
            <Icon icon-name="user-circle-outline" icon-class="text-muted"></Icon>
          </router-link>
        </div>
      </div>

      <div class="pt-3 pb-3">
        <div class="display-1">{{ $t('CustomerOverviewPage.title') }}</div>
        <div v-if="state.currentUser.referenceNumber" id="customer-overview-page-reference-number">
          {{ $t('CustomerOverviewPage.customerReferenceNumber') }}
          <span class="font-weight-bold">{{ state.currentUser.referenceNumber }}</span>
        </div>
      </div>

      <div v-if="isBackToAppEnabled">
        <button @click="onDynamicLinkClick" class="btn btn-lg btn-primary btn-block mb-3" id="customer-overview-page-close">
          {{ $t('CustomerOverviewPage.toApp') }}
        </button>
      </div>

      <template v-if="loading">
        <LoanCardSkeleton></LoanCardSkeleton>
        <LoanCardSkeleton></LoanCardSkeleton>
      </template>
      <template v-else>
        <CreditLimit :creditLimit="creditLimit" v-if="creditLimit != null"/>
        <Alert type="info" class="mb-0" v-if="isPageEmpty">
          <div v-html="$t('CustomerOverviewPage.noOffers')"></div>
        </Alert>
        <template v-else>
          <PalCard v-if="showPalCard" :palMaxAmount="automaticDecisionLimits.palMaxAmount"/>
          <SignMastercardAgreement/>
          <CashPalBanner v-if="isEligibleForPal" :palMaxAmount="automaticDecisionLimits.palMaxAmount"/>
          <WalletBalance v-if="showWalletBalance" :balance="walletBalance"
                         :isEligibleForNewLoan="isEligibleForNewLoan"/>
          <RepaymentBalance v-if="showRepaymentBalance" :balance="repaymentBalance"/>
          <router-link v-if="!showWalletBalance && isNewCoreEnabled && isEligibleForNewLoan" :to="{name: 'new-loan'}"
                       id="button-new-loan"
                       class="btn btn-sm rounded-pill btn-outline-primary btn-with-icon mt-2 mb-3 mr-3 mr-xxs-1">
            <Icon icon-class="icon-sm mr-1 ml-n1" icon-name="plus-outline"/>
            <span class="nav-text line-height-1">{{ $t('WalletBalance.newLoan') }}</span>
          </router-link>
          <LoansList v-if="loans && pendingApplications && applicationsHistory"
                     :loans="loans" :pending-applications="pendingApplications"
                     :applications-history="applicationsHistory"
                     :showFullDetailsForWrittenOff="showFullDetailsForWrittenOff"></LoansList>
          <Offers :offers="offers" :generic-offers="genericOffers" :promotions="promotions"></Offers>
        </template>
      </template>
    </pull-refresh>
    <div class="flex-grow-1"></div>
    <BottomNav></BottomNav>
  </div>
</template>

<script>
import store from '@shared/store'
import settings from '@shared/settings'
import Icon from '@shared/components/Icon'
import LogoElement from '@shared/components/LogoElement'
import Offers from '@shared/pages/overview/Offers'
import LoansList from '@shared/components/LoansList'
import LoanCardSkeleton from '@shared/components/LoanCardSkeleton'
import Alert from '@shared/components/Alert'
import PullRefresh from '@shared/components/PullRefresh'
import CreditLimit from '@shared/components/CreditLimit'
import CacheMixin from '@shared/mixins/CacheMixin'
import BottomNav from '../components/BottomNav'
import DynamicLinkMixin from '@shared/mixins/DynamicLinkMixin'
import WalletBalance from '@shared/components/wallet/WalletBalance'
import RepaymentBalance from '@shared/components/wallet/RepaymentBalance'
import UserMixin from '@shared/mixins/UserMixin'
import SignMastercardAgreement from '@shared/components/SignMastercardAgreement'
import CashPalBanner from '@shared/components/CashPalBanner'
import PalCard from '../components/PalCard'

export default {
  name: 'CustomerOverviewPage',
  components: {
    PalCard,
    CashPalBanner,
    SignMastercardAgreement,
    BottomNav,
    LoanCardSkeleton,
    LoansList,
    Offers,
    LogoElement,
    Icon,
    Alert,
    PullRefresh,
    CreditLimit,
    WalletBalance,
    RepaymentBalance
  },
  props: ['showFullDetailsForWrittenOff'],
  mixins: [CacheMixin, DynamicLinkMixin, UserMixin],
  data() {
    return {
      automaticDecisionLimits: { isEligibleForPal: false },
      loans: undefined,
      pendingApplications: undefined,
      applicationsHistory: undefined,
      offers: [],
      genericOffers: [],
      promotions: [],
      state: store.state,
      creditLimit: null,
      walletBalance: null,
      repaymentBalance: null,
      customerIsEligibleForNewLoan: false,
      loading: true,
      keepReloading: false,
      reloadingTimeout: undefined
    }
  },
  computed: {
    isPageEmpty() {
      return !this.isNewCoreEnabled && this.isEmpty(this.loans) && this.isEmpty(this.pendingApplications) && this.isEmpty(this.applicationsHistory) &&
          this.isEmpty(this.offers) && this.isEmpty(this.genericOffers) && this.isEmpty(this.promotions)
    },
    isBackToAppEnabled() {
      return settings.featureFlags.isDeepLinkingEnabled() && settings.isIOSWeb()
    },
    isNewCoreEnabled() {
      return this.$configuration.newCoreEnabled
    },
    isEligibleForNewLoan() {
      return this.customerIsEligibleForNewLoan &&
          this.pendingApplications !== undefined &&
          this.pendingApplications.length === 0 &&
          settings.featureFlags.isLoanApplicationEnabled()
    },
    showWalletBalance() {
      if (!this.isNewCoreEnabled || !this.walletBalance) return false
      return this.walletBalance.amount > 0 || this.walletBalance.recentTransactions?.length > 0
    },
    showRepaymentBalance() {
      return this.isNewCoreEnabled && this.repaymentBalance && this.repaymentBalance.amount && this.repaymentBalance.amount !== 0
    },
    isEligibleForPal() {
      return this.automaticDecisionLimits.isEligibleForPal &&
          this.pendingApplications !== undefined &&
          this.pendingApplications.every(a => a.productType === 'DEALER') &&
          settings.featureFlags.isLoanApplicationEnabled()
    },
    showPalCard() {
      return settings.featureFlags.isFastCheckoutEnabledGuard() && this.isEligibleForPal
    }
  },
  methods: {
    onRefresh() {
      this.loading = true
      clearTimeout(this.reloadingTimeout)
      return this.reloadData().then(() => this.keepReloadingRegularly())
    },
    reloadData() {
      return this.resetCache()
        .then(() => this.getAndStoreCustomerProfile(this.state.currentUser.pin))
        .then(() => this.loadData())
    },
    async loadData() {
      try {
        const response = await this.$http.get(`/api/customers/${this.state.currentUser.pin}/overview`)
        const overview = response.data
        this.loans = overview.loans
        this.pendingApplications = overview.pendingApplications
        this.applicationsHistory = overview.applicationsHistory
        this.offers = overview.offers || []
        this.promotions = overview.promotions || []
        this.genericOffers = overview.genericOffers || []
        this.creditLimit = overview.creditLimit
        if (this.$configuration.newCoreEnabled) {
          this.walletBalance = overview.walletBalance
          this.repaymentBalance = overview.repaymentBalance
          this.customerIsEligibleForNewLoan = overview.customerIsEligibleForNewLoan
          this.automaticDecisionLimits = overview.automaticDecisionLimits
        }
        this.loading = false
        store.setPendingApplications(this.pendingApplications)
        store.setLoans(this.loans)
        if (this.walletBalance) {
          store.setWalletBalance(this.walletBalance)
        }
      } catch (e) {
        console.error('first overview request failed', e)
      }
    },
    keepReloadingRegularly() {
      clearTimeout(this.reloadingTimeout)
      if (this.keepReloading) {
        const self = this
        self.reloadingTimeout = setTimeout(async () => {
          await self.resetCacheOverview()
          await this.loadData()
          self.keepReloadingRegularly()
        }, self.reloadingDataTimeoutMilliseconds())
      }
    },
    reloadingDataTimeoutMilliseconds() {
      if (this.pendingApplications === undefined || this.pendingApplications.length > 0) return 5_000
      // if (this.loans.map(l => l.status).includes('DELAYED')) return 5_000
      return 60_000
    },
    isEmpty(arr) {
      return !arr || arr.length === 0
    },
    onDynamicLinkClick() {
      this.redirectToApp('/customer-overview')
    }
  },
  async mounted() {
    if (this.$configuration.newCoreEnabled && !this.state.currentUser.referenceNumber) {
      await this.getAndStoreCustomerProfile(this.state.currentUser.pin)
    }

    await this.loadData()
    this.keepReloading = true
    this.keepReloadingRegularly()
  },
  destroyed() {
    this.keepReloading = false
    clearTimeout(this.reloadingTimeout)
  }
}
</script>
